/**
 * @fileoverview Module for `src/admin/EmailPreferences.js`.
 */

import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import Error from '@material-ui/icons/Error';
import axios from 'axios';
import {
  List,
  Datagrid,
  TextField,
  BooleanField,
  TextInput,
  Edit,
  DateField,
  Toolbar,
  SaveButton,
  SelectInput,
  BooleanInput,
  BulkExportButton,
  AutocompleteInput,
  Pagination,
  ReferenceInput,
  SimpleForm,
  NumberInput,
  EditButton,
  Filter,
} from 'react-admin';
import propTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import Warning from '@material-ui/icons/Warning';

import logger from '../lib/logger';
import { useAuth } from '../contexts/AuthContext';

/**
 * Pagination controls for email preferences list views.
 *
 * @param {Object} props
 * @returns {JSX.Element} Pagination component.
 */
const EmailPreferencesPagination = (props) => <Pagination rowsPerPageOptions={ [10, 25, 50, 100, 500] } { ...props } />;

/**
 * Bulk action controls for email preference lists.
 *
 * @param {Object} props
 * @returns {JSX.Element} Bulk action button group.
 */
const EmailPreferencesBulkActionButtons = (props) => (
  <BulkExportButton { ...props } />
);

/**
 * Filter controls for email preference and user metadata queries.
 *
 * @param {Object} props
 * @returns {JSX.Element} Email preference filters.
 */
const EmailPreferencesFilter = (props) => (
  <Filter { ...props }>
    <TextInput alwaysOn label='Email' source='email:contains' />
    <BooleanInput defaultValue source='newsletter' />
    <BooleanInput defaultValue source='labs' />
    <BooleanInput defaultValue source='reminders' />
    <BooleanInput defaultValue source='unsubscribe' />
    <BooleanInput defaultValue source='receiveFewer' />
    <ReferenceInput
      filterToQuery={ (slug) => ({ 'slug:contains': slug }) }
      label='Group'
      reference='Groups'
      sort={{ field: 'slug', order: 'ASC' }}
      source='groups.id'
    >
      <AutocompleteInput optionText='slug' />
    </ReferenceInput>
    <SelectInput
      choices={ [
        { id: 'banned', name: 'banned' },
        { id: 'pre_qualified', name: 'pre_qualified' },
        { id: 'qualified', name: 'qualified' },
        { id: 'super_qualified', name: 'super_qualified' },
      ] }
      label='Qual State'
      source='users.qualState'
    />
    <TextInput label='State' source='users.state' />
    <NumberInput label='Prepped Voter Count >= to' source='users.preppedVoterCount:greaterThanOrEqualTo' />
  </Filter>
);

/**
 * List view for user email preferences.
 *
 * @param {Object} props
 * @returns {JSX.Element} Email preferences list.
 */
export const EmailPreferencesList = (props) => (
  <List { ...props } bulkActionButtons={ <EmailPreferencesBulkActionButtons /> } filters={ <EmailPreferencesFilter /> } pagination={ <EmailPreferencesPagination /> } title='Email Preferences'>
    <Datagrid>
      <TextField source='id' />
      <TextField label='Email' source='email' />
      <TextField label='Auth0 Id' source='auth0Id' />
      <DateField source='createdAt' />
      <BooleanField source='newsletter' />
      <BooleanField source='labs' />
      <BooleanField source='reminders' />
      <BooleanField source='unsubscribe' />
      <BooleanField source='receiveFewer' />
      <EditButton { ...props } />
    </Datagrid>
  </List>
);

/**
 * Edit toolbar for email preference records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Toolbar with save action.
 */
const EmailPreferencesEditToolbar = (props) => (
  //removes default delete option from toolbar
  <Toolbar { ...props } >
    <SaveButton />
  </Toolbar>
);

/**
 * Inline utility for generating secure account links from email preference records.
 */
export class SecureLink extends Component {
  /**
   * Initializes state for the secure link generator: link type, generated
   * URL, visibility flags, and error state.
   *
   * @param {Object} props - Component props.
   * @param {Object} props.record - The email preferences record.
   * @param {Object} props.auth - Auth client for obtaining access tokens.
   */
  constructor(props) {
    super(props);
    this.state = {
      emailLinkType: null,
      emailLinkUrl: null,
      showEmailLinkForm: false,
      apiError: '',
      showEmailHelp: false,
    };
  }

  /**
   * Closes the email help alert panel.
   */
  hideEmailHelp = () => {
    this.setState({ showEmailHelp: false });
  };

  static get propTypes() {
    return {
      record: propTypes.object,
      auth: propTypes.object,
    };
  }

  /**
   * Toggles visibility of the email troubleshooting help panel.
   */
  handleToggleEmailHelp = () => this.setState({ showEmailHelp: !this.state.showEmailHelp });

  /**
   * Renders the secure link section: a help toggle, an expandable alert
   * with instructions and "last resort" confirmation, and the link
   * generator controls.
   *
   * @returns {JSX.Element} The secure link section.
   */
  render() {
    const emailLinkGenerator = this.renderEmailLinkGenerator(this.props);
    return (
      <React.Fragment>
        <div>
          <p>
            { this.state.emailLinkWarned && emailLinkGenerator }
          </p>
        </div>

        <div>
          { !this.state.emailLinkWarned
          && (
            <p>
              <span className='ms-3'>(click{ ' ' }
                <span
                  className='btn-link'
                  onClick={ this.handleToggleEmailHelp }
                  style={{ cursor: 'pointer' }}
                >
                  here
                </span>{ ' ' }
                if the user is having trouble receiving email)
              </span>
              <span className={ [this.state.showEmailHelp ? '' : 'hidden'] }>
                <Alert
                  action={
                    <Button
                      color='inherit'
                      onClick={ this.hideEmailHelp }
                      size='small'
                    >
                      CLOSE
                    </Button>
                  }
                  severity='info'
                >
                  Please confirm the user has checked their spam folder
                  and added “hello@votefwd.org” to their contact list. As a last resort,
                  you can generate a secure link and email it to them directly.
                  Be careful with this link, and use it sparingly.
                  Type “last resort” to generate a secure link: <input onChange={ (event) => { this.setState({ showEmailLinkForm: event.target.value === 'last resort' }); } } type='text' />.
                  { this.renderEmailLinkGenerator() }
                </Alert>
              </span>
            </p>
          ) }
        </div>
      </React.Fragment>
    );
  }

  /**
   * Calls the API to generate a secure email link of the selected type
   * for the current record, storing the resulting URL or error in state.
   *
   * @returns {Promise<void>} Resolves after the link URL or error is set.
   */
  generateEmailLink = async () => {
    try {
      const accessToken = await this.props.auth.getAccessToken();
      const res = await axios({
        method: 'POST',
        withCredentials: true,
        url: `${ process.env.REACT_APP_API_URL }/v1/emailPreferences/${ this.props.record.id }/secureEmailLink`,
        headers: {
          Authorization: 'Bearer '.concat(accessToken),
        },
        data: { purpose: this.state.emailLinkType },
      });
      this.setState({ apiError: '', emailLinkUrl: res.data.url });
    } catch (err) {
      logger.error(err);
      this.setState({
        apiError:
        (err.response && err.response.data && err.response.data.message)
        || err.message
        || 'An error occurred. Please try again.',
      });
    }
  };

  /**
   * Renders secure-link generation controls and result state.
   *
   * @returns {JSX.Element} Secure link generator form.
   */
  renderEmailLinkGenerator() {
    return (
      <div className={ this.state.showEmailLinkForm ? '' : 'hidden' }>
        <label className='mb-3 mt-3'>Type of secure link: </label>
        <select
          className='form-control ms-5'
          onChange={ (event) => this.setState({ emailLinkType: event.target.value, emailLinkUrl: null }) }
          style={{ width: '200px' }}
          value={ this.state.emailLinkType }
        >
          <option value=''> </option>
          <option value='forgot.password'>Forgot password</option>
          <option value='register.user'>Register User</option>
          <option value='verify.email'>Verify Email</option>
          <option value='email.preferences'>Update Preferences</option>
        </select>

        <Button
          className='ms-5 mt-2'
          color='inherit'
          disabled={ this.state.emailLinkType === null }
          onClick={ () => {
            this.generateEmailLink();
          } }
        >
          Generate Secure Link
        </Button>
        { this.state.emailLinkUrl
          && (
            <div className='ms-5' style={{ width: '500px', wordWrap: 'break-word' }}>
              <label>
                Here is the{ ' ' }{ this.state.emailLinkType.replace('.', ' ') }{ ' ' }
                link for <strong>{ this.props.record.email }</strong>
              </label>:
              <br />
              <Warning />
              <strong>Do not click on this link yourself. Only copy/paste it into an email for { this.props.record.email }.</strong>
              <br />
              { this.state.emailLinkUrl }
            </div>
          ) }
        { this.state.apiError
          && (
            <Fragment>
              <br />
              <Error /><strong>{ this.state.apiError }</strong>
            </Fragment>
          ) }
      </div>
    );
  }
}

/**
 * Edit view for email preference records.
 *
 * @param {Object} props
 * @returns {JSX.Element} Email preferences editor.
 */
export const EmailPreferencesEdit = (props) => {
  const auth = useAuth();
  return (
    <Edit undoable={ false } { ...props }>
      <SimpleForm toolbar={ <EmailPreferencesEditToolbar /> }>
        <TextInput disabled source='id' />
        <TextInput disabled source='email' />
        <SecureLink auth={ auth } />
        <TextInput disabled source='auth0Id' />
        <BooleanInput source='newsletter' />
        <BooleanInput source='labs' />
        <BooleanInput source='reminders' />
        <BooleanInput source='unsubscribe' />
        <BooleanInput source='receiveFewer' />
      </SimpleForm>
    </Edit>
  );
};


export default EmailPreferencesList;
